import { Label, Radio, RadioGroup } from "@headlessui/react";
import clsx from "clsx";

interface Props {
  color?: string;
  fill?: boolean;
  fillRadius?: number;
  className?: string;
}

const CircleIcon: React.FC<Props> = ({ fill, fillRadius, className }) => (
  <svg viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg" className={className}>
    <g strokeWidth="0" />
    <g strokeLinecap="round" strokeLinejoin="round" />
    <g>
      <path
        fill="current"
        stroke="none"
        d="M0 16q0 3.264 1.28 6.208t3.392 5.12 5.12 3.424 6.208 1.248 6.208-1.248 5.12-3.424 3.392-5.12 1.28-6.208-1.28-6.208-3.392-5.12-5.088-3.392-6.24-1.28q-3.264 0-6.208 1.28t-5.12 3.392-3.392 5.12-1.28 6.208zM4 16q0-3.264 1.6-6.016t4.384-4.352 6.016-1.632 6.016 1.632 4.384 4.352 1.6 6.016-1.6 6.048-4.384 4.352-6.016 1.6-6.016-1.6-4.384-4.352-1.6-6.048z"
      />
      <circle cx="16" cy="16" r={`${fillRadius || 16}`} fill={fill ? "current" : "none"} stroke="none" />
    </g>
  </svg>
);

interface InternalRadioGroupOptionProps {
  value: string;
  title: ({ checked }: { checked: boolean }) => React.ReactNode;
  description?: ({ checked }: { checked: boolean }) => React.ReactNode;
}

const InternalRadioGroupOption: React.FC<InternalRadioGroupOptionProps> = ({ value, title, description }) => {
  return (
    <Radio
      value={value}
      className={({ checked }) =>
        clsx([
          "relative",
          "flex",
          "justify-between",
          "cursor-pointer",
          "rounded-lg px-5 py-4 shadow-md focus:outline-none",
          { "border border-gray-200 bg-white": !checked },
          { "bg-brand-900/75 text-white": checked },
        ])
      }
    >
      {({ checked }) => (
        <>
          <div className="flex-col">
            <h1 className={clsx()}>{title({ checked })}</h1>
            {description && <p className={clsx("text-sm", { "": checked })}>{description({ checked })}</p>}
          </div>
          <CircleIcon
            className={clsx("w-[20px]", {
              "fill-white": checked,
              "fill-gray-300": !checked,
            })}
            fill={checked}
            fillRadius={8}
          />
        </>
      )}
    </Radio>
  );
};

type InternalRadioGroupProps<V> = React.ComponentProps<typeof RadioGroup<"div", V>> & {
  label?: React.ReactNode;
};

interface RadioGroupComponent {
  <V>(props: React.PropsWithChildren<InternalRadioGroupProps<V>>): React.JSX.Element;
  Option: typeof InternalRadioGroupOption;
}

function InternalRadioGroup<V>({
  label,
  value,
  onChange,
  children,
}: React.PropsWithChildren<InternalRadioGroupProps<V>>) {
  return (
    <RadioGroup value={value} onChange={onChange}>
      <div className="space-y-2">
        <Label>{label}</Label>
        {children}
      </div>
    </RadioGroup>
  );
}

(InternalRadioGroup as RadioGroupComponent).Option = InternalRadioGroupOption;
export { InternalRadioGroupOption as Option, InternalRadioGroup as RadioGroup };
